import { auth } from "@/lib/firebase";
import { ensureOwnerDoc } from "@/lib/onboardingBusiness";
import { onAuthStateChanged, type User } from "firebase/auth";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type OwnerAuthContextValue = {
  authInitialized: boolean;
  user: User | null;
  ownerReady: boolean;
  ownerError: boolean;
};

const OwnerAuthContext = createContext<OwnerAuthContextValue | null>(null);

export function OwnerAuthProvider({ children }: { children: React.ReactNode }) {
  const [authInitialized, setAuthInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [ownerReady, setOwnerReady] = useState(false);
  const [ownerError, setOwnerError] = useState(false);
  const generationRef = useRef(0);

  useEffect(() => {
    let alive = true;

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      const generation = generationRef.current + 1;
      generationRef.current = generation;

      if (!alive) return;

      setUser(nextUser);
      setAuthInitialized(true);
      setOwnerReady(false);
      setOwnerError(false);

      if (!nextUser) return;

      void ensureOwnerDoc(nextUser)
        .then(() => {
          if (!alive || generationRef.current !== generation) return;
          setOwnerReady(true);
        })
        .catch(() => {
          if (!alive || generationRef.current !== generation) return;
          console.warn("Owner account preparation failed.");
          setOwnerError(true);
        });
    });

    return () => {
      alive = false;
      generationRef.current += 1;
      unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({ authInitialized, user, ownerReady, ownerError }),
    [authInitialized, user, ownerReady, ownerError]
  );

  return (
    <OwnerAuthContext.Provider value={value}>
      {children}
    </OwnerAuthContext.Provider>
  );
}

export function useOwnerAuth() {
  const value = useContext(OwnerAuthContext);
  if (!value) {
    throw new Error("useOwnerAuth must be used inside OwnerAuthProvider");
  }
  return value;
}
